import type {Point} from 'highcharts';

import {ButtonOption} from 'common/components/popover-card/types';
import {BarType, SelectedBar} from 'common/report/insights/charts/funnels-step-chart/types';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {FunnelBookmark} from 'common/report/funnels/models/types';
import {FunnelQueryResponse} from 'common/report/funnels/models/api-types';
import {FunnelsQuery} from 'common/report/funnels/models/funnels-query';
import {Cohort} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {CohortDetail} from 'common/widgets/create-cohort-button';
import {CalendarDateRange} from 'common/widgets/date-range-picker/models/types';
import {SlottedContent} from 'common/report/funnels/visualization/chart-display/utils/popover';

// TODO rename this as the line charts also use it, maybe e.g. TtcFreqPopoverSelection
export interface BarChartPopover {
  // Required to uniquely identify the segment
  segment: {
    count: number;
    label: string;
    name: string | symbol;
    color: string;
  };
  // The label for the bar
  // Ex: for ttc, the label for the time to convert period (e.g. "0 - 2 days")
  // Ex: for frequency, the label for the frequency, i.e. number of times
  rowLabel: string;
  // The index for the bar
  // Ex: for ttc, the index of this time period within the ttc period (e.g. if the interval is [0, 10] with a step of [1 day],
  // the 0 index would be "0 - 1 day" and the 3 index would be "3 - 4 days"
  // Ex: for frequency, the index of this frequency segment (i.e. 1 time is index 0, 4 times is index 3)
  queryIndex?: number;
  cohortDetails?: CohortDetail[];
  cohort?: Cohort;

  clientX?: number;
  clientY?: number;

  /** the selected point, if this is a line chart */
  point?: Point;
}

export enum ViewUsersType {
  Converted = `view-users-converted`,
  ViewUsers = `view-users`,
  DroppedOff = `view-users-dropped-off`,
}

export enum StepPopoverAction {
  ViewInFlows = `view-in-flows`,
  ViewUsers = `view-users`,
  ViewReplays = `view-replays`,
}

export interface StepPopoverActionParams {
  action: StepPopoverAction;
  stepIndex: number;
  segmentProp: string;
  isDropOff: boolean;
  color: string;
  metricLabel: string;
  barType: BarType;
  isComparison: boolean;
  comparisonDateRange: CalendarDateRange;
}

export type PopoverParams = {
  /** whether popover is open on the chart */
  open: boolean;
  /** which bar on the chart the popover is targeting */
  bar?: SelectedBar;
  groupByValue?: string;
  /** descriptors of the cohorts which can be created from this bar */
  cohortDetails?: CohortDetail[];
  cohort?: Cohort;
  /** positioning information for the popover */
  styles?: any;
  isSegmented?: boolean;
  isDropOff?: boolean;
  /** displayable segment for the selected bar */
  segmentProp?: string;
  /** header for the popover */
  title?: string;
  /**
   * available actions within the popover
   *
   * TODO for values because it's an akward mix of types at the moment
   */
  /** text to be displayed in the popover */
  buttonOptions?: ButtonOption[];
  slottedContent?: SlottedContent[];

  onAction?: (ev: CustomEvent<{action: string; item: any}>) => void; // TODO typing kinda loose here
  onClose?: () => void;
};

interface FunnelsChartDisplayAttrs {
  'resizing-layout': boolean;
  'bookmark-name': string;
  'rca-source-id'?: number;
}

interface FunnelsChartDisplayProps {
  bookmarkParams: FunnelBookmark;
  queryResults: FunnelQueryResponse;
  dataGroups: DataGroup[];
}

export type UpdateEvent = CustomEvent<{delta: Partial<FunnelsQuery>}>;
export type TrackEvent = CustomEvent<{eventName: string; props: TODO}>;
export interface FunnelsChartDisplayJsxProps {
  attrs: FunnelsChartDisplayAttrs;
  props: FunnelsChartDisplayProps;
  on?: {
    update: (ev: UpdateEvent) => void;
    track: (ev: TrackEvent) => void;
  };
}
